/**
 * Whether `value` has anything in it a person can see (steward-app#943).
 *
 * `safeDisplayText` strips what is never legitimate, but it deliberately leaves
 * ZWNJ, ZWJ, variation selectors and combining marks alone, because each is part of
 * real text (Persian, emoji sequences, Vietnamese, Devanagari). A value made ONLY of
 * those survives the strip, satisfies `min(1)`, and is stored as a name that renders
 * blank. This is the one place that says what "something visible" means.
 *
 * The rule: remove whitespace, control and format characters (`\p{Cc}`, `\p{Cf}`),
 * every mark (`\p{M}`, which covers combining marks and the variation selectors) and
 * every Default_Ignorable_Code_Point (which adds things like the Hangul fillers and
 * the Mongolian free variation selectors); if at least one code point remains, the
 * string is visible. A mark after a visible base is therefore fine, because the base
 * is what remains, and a name in a mark-heavy script is never refused for its marks.
 *
 * Not covered, on purpose: look-alike blanks that are ordinary visible-category code
 * points (for example U+2800 BRAILLE PATTERN BLANK). Those are spoof territory (#600),
 * not "renders as nothing" territory, and are not decided here.
 *
 * Property escapes need the `u` flag and ES2018. steward-types and the façade API both
 * set `target: ES2022` in their tsconfig (read 2026-10-03), and Node 24 ran the tests.
 */
const NOT_VISIBLE = /[\s\p{Cc}\p{Cf}\p{M}\p{Default_Ignorable_Code_Point}]/gu;

export function hasVisibleContent(value: string): boolean {
  return value.replace(NOT_VISIBLE, '').length > 0;
}
